/**
 * useLiveStatus — React hook that tracks the `ManagedLive` singleton's
 * connection state for use in UI affordances (top-bar status dot, etc.).
 *
 * The hook is deliberately lightweight: it subscribes once to
 * `onState()` and returns the current state, defaulting to `'idle'`
 * when the singleton hasn't been created yet. The hook does not
 * trigger a connection — that remains the responsibility of the
 * screens that use live data (e.g. `useLiveData`).
 */
import { useEffect, useState } from 'react';
import { getRmfLive } from '../zenoh-sse';
import type { FetchSseState, LiveState } from '../zenoh-sse';

export function useLiveStatus(): LiveState {
  const [state, setState] = useState<LiveState>('idle');

  useEffect(() => {
    let unsub: (() => void) | null = null;
    try {
      const live = getRmfLive();
      // Map FetchSseState to the legacy LiveState union (the
      // 'recovering' v1 state is rendered as 'stale' in the legacy
      // UI affordances until the screens are migrated).
      unsub = live.onState((s: FetchSseState) =>
        setState(s === 'recovering' ? 'stale' : s),
      );
    } catch {
      // Singleton not initialised yet (e.g. apiBaseUrl missing); keep 'idle'.
    }
    return () => {
      unsub?.();
    };
  }, []);

  return state;
}
